import { alpha, Badge, Box, styled, Tab, Tabs, Typography, type SxProps, type Theme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "orientation",
})<{ variant?: TabVariant; orientation?: "horizontal" | "vertical" }>(({ theme, variant, orientation }) => {
  const effectiveVariant = variant ?? "standard" as TabVariant;
  const baseStyles = {
    minHeight: effectiveVariant === "compact" ? 36 : 48,
    "& .MuiTabs-indicator": {
      height: effectiveVariant === "pills" ? 0 : 3,
      borderRadius: effectiveVariant === "pills" ? 0 : "3px 3px 0 0",
      backgroundColor: theme.palette.primary.main,
      transition: "all 0.3s ease-in-out",
    },
    "& .MuiTabs-flexContainer": {
      gap: effectiveVariant === "pills" ? theme.spacing(1) : 0,
    },
  }

  if (effectiveVariant === "pills") {
    return {
      ...baseStyles,
      "& .MuiTabs-indicator": {
        display: "none",
      },
    }
  }

  if (effectiveVariant === "underlined") {
    return {
      ...baseStyles,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "& .MuiTabs-indicator": {
        bottom: 0,
        height: 2,
      },
    }
  }

  if (effectiveVariant === "contained") {
    return {
      ...baseStyles,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      borderRadius: theme.spacing(1),
      padding: theme.spacing(0.5),
      "& .MuiTabs-indicator": {
        display: "none",
      },
    }
  }

  return baseStyles
})

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "size" && prop !== "hasIcon" && prop !== "hasBadge",
})<{
  variant?: TabVariant
  size?: TabSize
  hasIcon?: boolean
  hasBadge?: boolean
}>(({ theme, variant, size, hasIcon, hasBadge }) => {
  const effectiveVariant = variant ?? "standard"
  const sizeStyles = {
    small: {
      minHeight: 32,
      padding: theme.spacing(0.5, 1.5),
      fontSize: "0.75rem",
    },
    medium: {
      minHeight: 40,
      padding: theme.spacing(1, 2),
      fontSize: "0.875rem",
    },
    large: {
      minHeight: 48,
      padding: theme.spacing(1.5, 3),
      fontSize: "1rem",
    },
    compact: {
      minHeight: 28,
      padding: theme.spacing(0.25, 1),
      fontSize: "0.75rem",
    },
  }

  const baseStyles = {
    textTransform: "none" as const,
    fontWeight: 500,
    borderRadius: theme.spacing(0.75),
    transition: "all 0.2s ease-in-out",
    minWidth: hasIcon && !hasBadge ? 48 : "auto",
    ...sizeStyles[size || "medium"],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
    },
    "&.Mui-selected": {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  }

  if (effectiveVariant === "pills") {
    return {
      ...baseStyles,
      border: `1px solid ${theme.palette.divider}`,
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
        },
      },
    }
  }

  if (effectiveVariant === "contained") {
    return {
      ...baseStyles,
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
        },
      },
    }
  }

  return baseStyles
})

const TabContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  minHeight: 200,
}))

export type TabVariant = "standard" | "pills" | "underlined" | "contained" | "compact"
export type TabSize = "small" | "medium" | "large" | "compact"
export type TabOrientation = "horizontal" | "vertical"

export interface TabItem {
  id: string
  label: string | React.ReactNode
  content?: React.ReactNode
  icon?: React.ReactNode
  badge?: number | string
  disabled?: boolean
  hidden?: boolean
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success"
  tooltip?: string
}

export interface CustomTabsProps {
  tabs: TabItem[]
  defaultValue?: string
  value?: string
  onChange?: (value: string) => void
  variant?: TabVariant
  size?: TabSize
  orientation?: TabOrientation
  centered?: boolean
  scrollable?: boolean
  allowEmpty?: boolean
  fullWidth?: boolean
  showContent?: boolean
  contentSx?: SxProps<Theme>
  tabsSx?: SxProps<Theme>
  className?: string
  ariaLabel?: string
  renderTabContent?: (tab: TabItem) => React.ReactNode
  onTabClick?: (tab: TabItem, index: number) => void
}

export const CustomTabs: React.FC<CustomTabsProps> = ({
  tabs,
  defaultValue,
  value: controlledValue,
  onChange,
  variant = "standard",
  size = "medium",
  orientation = "horizontal",
  centered = false,
  scrollable = false,
  allowEmpty = false,
  fullWidth = false,
  showContent = true,
  contentSx,
  tabsSx,
  className,
  ariaLabel = "Custom tabs",
  renderTabContent,
  onTabClick,
}) => {
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue || (tabs.length > 0 && !allowEmpty ? tabs[0].id : ""),
  )

  const currentValue = controlledValue !== undefined ? controlledValue : internalValue
  const isControlled = controlledValue !== undefined

  const visibleTabs = useMemo(() => tabs.filter((tab) => !tab.hidden), [tabs])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue)
      }
      onChange?.(newValue)
    },
    [isControlled, onChange],
  )

  const handleTabClick = useCallback(
    (tab: TabItem, index: number) => {
      onTabClick?.(tab, index)
    },
    [onTabClick],
  )

  const currentTab = useMemo(() => visibleTabs.find((tab) => tab.id === currentValue), [visibleTabs, currentValue])

  const renderTabLabel = useCallback(
    (tab: TabItem) => {
      if (React.isValidElement(tab.label)) {
        return tab.label
      }

      const hasIcon = !!tab.icon
      const hasBadge = tab.badge !== undefined && tab.badge !== null

      if (!hasIcon && !hasBadge) {
        return tab.label
      }

      return (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            "& .MuiChip-root": {
              height: size === "small" || size === "compact" ? 16 : 20,
              fontSize: size === "small" || size === "compact" ? "0.625rem" : "0.75rem",
            },
          }}
        >
          {hasIcon && tab.icon}
          {tab.label && (
            <Typography
              variant="inherit"
              component="span"
              sx={{
                display: hasIcon && size === "compact" ? { xs: "none", sm: "block" } : "block",
              }}
            >
              {tab.label}
            </Typography>
          )}
          {hasBadge && (
            <Badge
              badgeContent={tab.badge}
              color={tab.color || "primary"}
              max={999}
              sx={{
                "& .MuiBadge-badge": {
                  position: "static",
                  transform: "none",
                  height: size === "small" || size === "compact" ? 16 : 18,
                  minWidth: size === "small" || size === "compact" ? 16 : 18,
                  fontSize: size === "small" || size === "compact" ? "0.625rem" : "0.75rem",
                },
              }}
            >
              <Box />
            </Badge>
          )}
        </Box>
      )
    },
    [size],
  )

  if (visibleTabs.length === 0) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">No tabs available</Typography>
      </Box>
    )
  }

  return (
    <Box className={className}>
      <StyledTabs
        value={currentValue || false}
        onChange={handleChange}
        variant={variant}
        orientation={orientation}
        centered={centered}
        scrollButtons={scrollable ? "auto" : false}
        allowScrollButtonsMobile={scrollable}
        aria-label={ariaLabel}
        sx={{
          ...tabsSx,
          ...(fullWidth && { width: "100%", height: '100% !important' }),
        }}
      >
        {visibleTabs.map((tab, index) => (
          <StyledTab
            key={tab.id}
            value={tab.id}
            label={renderTabLabel(tab)}
            disabled={tab.disabled}
            variant={variant}
            size={size}
            hasIcon={!!tab.icon}
            hasBadge={tab.badge !== undefined}
            onClick={() => handleTabClick(tab, index)}
            title={tab.tooltip}
            sx={{
              ...(fullWidth && { flex: 1 }),
            }}
          />
        ))}
      </StyledTabs>

      {showContent && currentTab && (
        <TabContent sx={contentSx}>{renderTabContent ? renderTabContent(currentTab) : currentTab.content}</TabContent>
      )}
    </Box>
  )
}

export const TabPresets = {
  navigation: {
    variant: "underlined" as TabVariant,
    size: "medium" as TabSize,
    fullWidth: true,
  },
  pills: {
    variant: "pills" as TabVariant,
    size: "medium" as TabSize,
    centered: true,
  },
  compact: {
    variant: "standard" as TabVariant,
    size: "compact" as TabSize,
    scrollable: true,
  },
  sidebar: {
    variant: "contained" as TabVariant,
    orientation: "vertical" as TabOrientation,
    size: "medium" as TabSize,
  },
} as const

export const useTabState = (initialTab?: string) => {
  const [activeTab, setActiveTab] = useState<string>(initialTab || "")

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab)
  }, [])

  return {
    activeTab,
    setActiveTab: handleTabChange,
  }
}

export const createTabItem = (
  id: string,
  label: string | React.ReactNode,
  options: Partial<Omit<TabItem, "id" | "label">> = {},
): TabItem => ({
  id,
  label,
  ...options,
})

export default CustomTabs